import React from 'react';
import moment from 'moment-timezone';
import classnames from 'classnames';

import SessionStore from 'stores/SessionStore';

import Badge from 'components/v2/Badge';
import CustomerTableCell from './CustomerTableCell';
import AppointmentStatus from './AppointmentStatus';
import AppointmentActionsTableCell from './AppointmentActionsTableCell';

const ADMIN = 'Admin';
const CARRIER = 'Carrier';
const CONSIGNEE = 'Consignee';

const ALL_USERS = [ADMIN, CARRIER, CONSIGNEE];
const ADMIN_ONLY = [ADMIN];
const ADMIN_CONSIGNEE = [ADMIN, CONSIGNEE];
const ADMIN_CARRIER = [ADMIN, CARRIER];
const APPOINTMENT_MODULE = 'AppointmentModule';

const isAdmin = SessionStore.getUserType() === 'Admin';

const COLUMNS = [
  {
    name: 'CONSIGNEE',
    users: ADMIN_CARRIER,
    attribute: 'consignee',
    show: true,
    sort: true,
    columnInfo: 'CONSIGNEE_DESCRIPTION',
    render: (value, row, userType) => {
      const clickable = userType === 'Admin';
      return (
        <td>
          <CustomerTableCell user={row.consignee} clickable={clickable} />
        </td>
      );
    },
  },
  {
    name: 'CONTAINER',
    users: ALL_USERS,
    attribute: 'container_number',
    show: true,
    sort: true,
    columnInfo: 'CONTAINER_DESCRIPTION',
    render: (value, row, userType) => {
      return <td>{value}</td>;
    },
  },
  {
    name: 'MASTER_BILL',
    users: ALL_USERS,
    attribute: 'master_bill_number',
    show: true,
    sort: true,
    columnInfo: 'MASTER_BILL_DESCRIPTION',
    render: (value, row, userType) => {
      return <td>{value}</td>;
    },
  },
  {
    name: 'DAYS_AT_FACILITY',
    localeNamespace: APPOINTMENT_MODULE,
    users: ALL_USERS,
    attribute: 'days_at_facility',
    show: false,
    sort: false,
    columnInfo: 'DAYS_AT_FACILITY_DESCRIPTION', // Check on this for localization. Kept pulling in the locale for 'DAYS_AT_FACILITY'
    render: (value, row, userType) => {
      function getDeliveredSinceDate(stop = {}) {
        const deliveredDate =
          stop?.timeline?.complete?.meta?.date || stop?.timeline?.complete?.at;

        return moment().diff(moment(deliveredDate), 'days');
      }

      const deliveredSince = getDeliveredSinceDate(row.yardStop);
      const deliveredSinceClassNames = classnames({
        'text-red': deliveredSince >= 3,
      });
      return (
        <td>
          <span className={deliveredSinceClassNames}>
            {`${deliveredSince}`}
          </span>
        </td>
      );
    },
  },
  {
    name: 'LAST_FREE_DAY',
    users: ALL_USERS,
    attribute: 'yardStop.lfd',
    show: false,
    sort: true,
    columnInfo: 'LFD_DESCRIPTION',
    render: (value, row, userType) => {
      const timezone = row?.yard?.timezone || 'America/Los_Angeles';
      return <td>{moment.tz(row?.yardStop?.lfd, timezone).format('L')}</td>;
    },
  },
  {
    name: 'CARRIER',
    users: ADMIN_CONSIGNEE,
    attribute: 'carrier',
    show: true,
    sort: true,
    columnInfo: 'CARRIER_DESCRIPTION',
    render: (value, row, userType) => {
      const clickable = userType === 'Admin';
      return (
        <td>
          <CustomerTableCell user={row.carrier} clickable={clickable} />
        </td>
      );
    },
  },
  {
    name: 'SHIPPER',
    users: ADMIN_ONLY,
    attribute: 'shipper',
    show: true,
    sort: true,
    columnInfo: 'SHIPPER_DESCRIPTION',
    render: (value, row, userType) => {
      const clickable = userType === 'Admin';
      return (
        <td>
          <CustomerTableCell user={row.shipper} clickable={clickable} />
        </td>
      );
    },
  },
  {
    name: 'YARD',
    users: ALL_USERS,
    attribute: 'yard',
    show: true,
    sort: true,
    columnInfo: 'YARD_DESCRIPTION',
    render: (value, row, userType) => {
      const yard = row.yard?.name || '';

      return (
        <td>
          <span>{yard}</span>
        </td>
      );
    },
  },
  {
    name: 'STATUS',
    users: ALL_USERS,
    attribute: 'status',
    show: true,
    sort: true,
    columnInfo: 'STATUS_DESCRIPTION',
    render: (value, row, userType) => {
      return (
        <td>
          <AppointmentStatus status={row.status} />
        </td>
      );
    },
  },
  {
    name: 'APPOINTMENT',
    users: ALL_USERS,
    attribute: 'window_start',
    show: true,
    sort: true,
    columnInfo: 'APPOINTMENT_DESCRIPTION',
    render: (value, row, userType) => {
      const timezone = row?.yard?.timezone || 'America/Los_Angeles';
      return (
        <td className="whitespace-pre">
          {moment.tz(row.window_start, timezone).format('L[\n]LT')}
        </td>
      );
    },
  },
  {
    name: 'STACK_POSITION',
    users: ADMIN_ONLY,
    attribute: 'stack_position',
    show: true,
    sort: true,
    columnInfo: 'STACK_POSITION_DESCRIPTION',
    render: (value, row, userType) => {
      return <td>{value}</td>;
    },
  },
  {
    name: 'RELEASE_CODE',
    localeNamespace: APPOINTMENT_MODULE,
    users: ALL_USERS,
    attribute: 'pin_code',
    show: true,
    sort: true,
    columnInfo: 'RELEASE_CODE_DESCRIPTION',
    render: (value, row, userType) => {
      const showReleaseFor = ['approved', 'complete'];
      const releaseCode =
        value && showReleaseFor.includes(row.status) ? (
          <Badge color="purple">{value}</Badge>
        ) : null;

      return <td>{releaseCode}</td>;
    },
  },
  {
    name: 'LAST_UPDATED',
    users: ALL_USERS,
    attribute: 'modified_at',
    show: false,
    sort: true,
    columnInfo: 'MODIFIED_AT_DESCRIPTION',
    render: (value, row, userType) => {
      return (
        <td className="whitespace-pre">
          {moment(row.modified_at).format('L[\n]LT')}
        </td>
      );
    },
  },
  {
    name: 'UPDATES',
    users: ALL_USERS,
    attribute: 'updates',
    show: true,
    sort: true,
    headerClassName: classnames({ 'right-52': isAdmin, 'right-32': !isAdmin }),
    columnInfo: 'UPDATES_DESCRIPTION',
    pinned: true,
    render: (value, row, userType, onEditActionClick, onStatusActionClick) => {
      const unread =
        row && row.comments
          ? row.comments.filter((comment) => {
              return comment.authorType !== userType && !comment.read;
            }).length
          : null;

      const cellClassNames = classnames({
        sticky: true,
        'bg-white': true,
        'right-52': isAdmin,
        'right-32': !isAdmin,
      });
      return (
        <td className={cellClassNames}>
          {unread > 0 && (
            <div
              className="flex align-middle cursor-pointer"
              onClick={() => onEditActionClick(row._id)}
            >
              <i className="fa fa-comment text-xl" />
              <Badge
                className="relative -top-2 -left-3"
                color="red"
                size="small"
              >
                {unread}
              </Badge>
            </div>
          )}
        </td>
      );
    },
  },
  {
    name: 'ACTIONS',
    users: ALL_USERS,
    attribute: 'actions',
    show: true,
    sort: false,
    columnInfo: 'ACTIONS_DESCRIPTION',
    pinned: true,
    render: (value, row, userType, onEditActionClick, onStatusActionClick) => {
      return (
        <td className="sticky right-0 bg-white">
          <AppointmentActionsTableCell
            appointment={row}
            onEditActionClick={onEditActionClick}
            onStatusActionClick={onStatusActionClick}
            userType={userType}
          />
        </td>
      );
    },
  },
];

export default COLUMNS;
